import React from "react";
import styled, { css } from "styled-components";
import { Card, Grid } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";

const { useBreakpoint } = Grid;

const statsData = [
  { value: "0", label: "Total Trips", color: "#800000" },
  { value: "0", label: "Distance Travelled", color: "#800000" },
  { value: "-", label: "Total Riders", color: "#800000" },
  { value: "-", label: "Active Riders", color: "#800000" },
  { value: "0", label: "Total Issues", color: "#800000" },
  { value: "0", label: "Pending Issues", color: "#800000" },
  { value: "0", label: "Net Swaps", color: "#800000" },
];

// Breakpoint mixins
const breakpoints = {
  extraSmall: "(max-width: 299px)",
  verySmall: "(min-width: 300px) and (max-width: 374px)",
  smallMobile: "(min-width: 375px) and (max-width: 575px)",
  mobile: "(min-width: 576px) and (max-width: 767px)",
  tablet: "(min-width: 768px) and (max-width: 991px)",
  desktop: "(min-width: 992px)",
};

const StatContainer = styled.div<{ $screenSize?: string }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  justify-content: center;
  align-items: center;
  margin: 0px;
  max-width: 400px;

  @media ${breakpoints.tablet} {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    max-width: 100%;
  }

  @media ${breakpoints.mobile} {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    max-width: 100%;
  }

  @media ${breakpoints.smallMobile} {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    max-width: 100%;
  }

  @media ${breakpoints.verySmall} {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    max-width: 100%;
  }

  @media ${breakpoints.extraSmall} {
    grid-template-columns: repeat(1, 1fr);
    gap: 4px;
    max-width: 100%;
  }
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100% !important;
  text-align: center;
  border-radius: 10px;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  .ant-card-body {
    padding: 16px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  @media ${breakpoints.tablet} {
    border-radius: 8px;
    box-shadow: 1px 1px 8px rgba(0, 0, 0, 0.08);
    .ant-card-body {
      padding: 14px;
    }
  }

  @media ${breakpoints.mobile} {
    border-radius: 8px;
    box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.08);
    .ant-card-body {
      padding: 12px;
    }
  }

  @media ${breakpoints.smallMobile} {
    border-radius: 6px;
    box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.06);
    .ant-card-body {
      padding: 10px 8px;
    }
  }

  @media ${breakpoints.verySmall} {
    border-radius: 4px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.05);
    .ant-card-body {
      padding: 8px 6px;
    }
  }

  @media ${breakpoints.extraSmall} {
    border-radius: 3px;
    box-shadow: none;
    border: 0.5px solid #e8e8e8;
    .ant-card-body {
      padding: 6px 4px;
    }
  }
`;

const Value = styled.div`
  font-size: 22px;
  font-weight: bold;
  line-height: 1.2;
  word-break: break-word;

  @media ${breakpoints.tablet} {
    font-size: 20px;
  }

  @media ${breakpoints.mobile} {
    font-size: 18px;
  }

  @media ${breakpoints.smallMobile} {
    font-size: 16px;
  }

  @media ${breakpoints.verySmall} {
    font-size: 14px;
  }

  @media ${breakpoints.extraSmall} {
    font-size: 12px;
  }
`;

const Label = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 4px;
  line-height: 1.3;

  @media ${breakpoints.tablet} {
    font-size: 11px;
    margin-top: 3px;
  }

  @media ${breakpoints.mobile} {
    font-size: 11px;
    margin-top: 3px;
  }

  @media ${breakpoints.smallMobile} {
    font-size: 10px;
    margin-top: 2px;
  }

  @media ${breakpoints.verySmall} {
    font-size: 9px;
    margin-top: 2px;
  }

  @media ${breakpoints.extraSmall} {
    font-size: 8px;
    margin-top: 1px;
  }
`;

export default function HorizontalTiles() {
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  const { totalKmDriven, totalTrips } = useSelector<AppState, AppState["trips"]>(
    (state) => state.trips
  );

  const { totalIssue, pendingCount } = useSelector<
    AppState,
    AppState["issues"]
  >((state) => state.issues);

  const { totalRiders, totalActiveRiders } = useSelector<
    AppState,
    AppState["vehicles"]
  >((state) => state.vehicles);

  const { netSwaps } = useSelector<
    AppState,
    AppState["billing"]
  >((state) => state.billing);

  const updatedStatsData = statsData.map((stat) => {
    if (stat.label === "Total Trips") {
      return { ...stat, value: totalTrips?.toString() || "0" };
    }
    if (stat.label === "Distance Travelled") {
      return { ...stat, value: `${totalKmDriven?.toString() || 0} KM` };
    }
    if (stat.label === "Total Issues") {
      return { ...stat, value: totalIssue?.toString() || "0" };
    }
    if (stat.label === "Pending Issues") {
      return { ...stat, value: pendingCount?.toString() || "0" };
    }
    if (stat.label === "Total Riders") {
      return { ...stat, value: totalRiders?.toString() || "0" };
    }
    if (stat.label === "Active Riders") {
      return { ...stat, value: totalActiveRiders?.toString() || "0" };
    }
      if (stat.label === "Net Swaps") {
      return { ...stat, value: netSwaps?.toString() || "0" };
    }
    return stat;
  });

  return (
    <StatContainer>
      {updatedStatsData.map((stat, index) => (
        <StatCard key={index}>
          <Value style={{ color: stat.color }}>{stat.value}</Value>
          <Label>{stat.label}</Label>
        </StatCard>
      ))}
    </StatContainer>
  );
}